import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "utils/dayjs";
import { Box, Button, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CreditCardIcon,
  Delete02Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Drawer } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import Summary from "components/common/Summary";
import AccountStatus from "enums/AccountStatus";
import { AstradaConnection } from "./types";
import CardNetworkMark from "./CardNetworkMark";
import ComplianceNotice from "./ComplianceNotice";
import ConnectionStatusBadge from "./ConnectionStatusBadge";

/**
 * Astrada has no sync state of its own to report, so this is derived from the
 * connection status rather than read. See README.md.
 */
const SYNC_STATUS_LABELS: Record<string, string> = {
  [AccountStatus.ACTIVE]: "SyncStatusActive",
  [AccountStatus.SUBMITTED]: "SyncStatusPending",
  [AccountStatus.DISCONNECTED]: "SyncStatusStopped",
  [AccountStatus.INACTIVE]: "SyncStatusPaused",
  [AccountStatus.REQUESTED]: "SyncStatusPending",
};

export interface ManageConnectionDrawerProps {
  open: boolean;
  /** Null while nothing is selected, so the drawer can stay mounted for its close animation. */
  connection: AstradaConnection | null;
  onClose: () => void;
  /** Fired once the slide-out finishes, so the page can drop its selection. */
  onAfterClose?: () => void;
  onDisconnect: () => void;
}

/** Read-only detail for one enrolled card program. See README.md. */
const ManageConnectionDrawer: React.FC<ManageConnectionDrawerProps> = ({
  open,
  connection,
  onClose,
  onAfterClose,
  onDisconnect,
}) => {
  const { t } = useTranslation();

  if (!connection) return null;

  const syncStatusKey = SYNC_STATUS_LABELS[connection.status];

  const rows: { label: string; value: React.ReactNode }[] = [
    { label: t("Program"), value: connection.name },
    {
      label: t("Network"),
      value: (
        <CardNetworkMark
          name={connection.astradaCard?.network || connection.name}
        />
      ),
    },
    {
      label: t("Cards"),
      value: t(
        connection.cardCount === 1 ? "{{count}} card" : "{{count}} cards",
        {
          count: connection.cardCount,
        },
      ),
    },
    {
      label: t("Connected"),
      value: connection.connectedAt
        ? dayjs(connection.connectedAt).format("MM/DD/YYYY")
        : "-",
    },
    { label: t("RPIC"), value: connection.networkReference ?? "-" },
    {
      label: t("Sync status"),
      value: syncStatusKey ? t(syncStatusKey) : connection.status,
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onAfterClose={onAfterClose}
      title={t("Program")}
      headerAction={<ConnectionStatusBadge status={connection.status} />}
    >
      <Stack gap="var(--space-5)">
        <Inline gap="var(--space-3)" align="center">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: "var(--radius-sm)",
              backgroundColor: "var(--color-bg-brand-subtle)",
            }}
          >
            <HugeiconsIcon
              icon={CreditCardIcon}
              size={18}
              color="var(--color-text-brand)"
            />
          </Box>
          <Stack gap="var(--space-1)">
            <Typography variant="h6">{connection.name}</Typography>
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("External card connection")}
            </Typography>
          </Stack>
        </Inline>

        <ComplianceNotice />

        <Summary
          items={rows.map((row) => ({ title: row.label, value: row.value }))}
        />

        <Box>
          <Button
            variant="danger-filled"
            onClick={onDisconnect}
            startIcon={
              <HugeiconsIcon
                icon={Delete02Icon}
                size={16}
                color="currentColor"
              />
            }
          >
            {t("Disconnect")}
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default ManageConnectionDrawer;
